"use client";

import Image from "next/image";
import { usePortalTranslations } from "../i18n/client";
import { usePortalPreferences } from "./PortalPreferences";
import type { BookingResource, BookingSlot, PublicClientPortal } from "../lib/clientPortalApi";
import { dateLabel, formatPrice, initials, resourcePrice, safePortalImageUrl, servicePrice, timeLabel, type ServiceChoice } from "../lib/portalPresentation";
import styles from "./ReservationSummary.module.css";

type ReservationSummaryProps = {
  portal: PublicClientPortal;
  selected?: ServiceChoice;
  resource?: BookingResource;
  slot?: BookingSlot;
  timezone: string;
  onChangeResource?: () => void;
  disabled?: boolean;
};

export function ReservationSummary({ portal, selected, resource, slot, timezone, onChangeResource, disabled = false }: ReservationSummaryProps) {
  const t = usePortalTranslations();
  const { locale } = usePortalPreferences();
  const photo = safePortalImageUrl(resource?.photoUrl);
  const staff = resource?.type === "staff";
  const price = selected
    ? resource ? formatPrice(resourcePrice(resource, selected.service), selected.service.currencyCode, locale) : servicePrice(selected.service, locale)
    : null;

  return <aside className={styles.summary} aria-label={t("reservation.summary.label")}>
    <h2>{t("reservation.summary.title")}</h2>
    <div className={styles.card}>
      <div className={styles.portal}>
        <strong>{portal.name}</strong>
        {portal.presentation?.address ? <p>{portal.presentation.address}</p> : null}
      </div>

      {resource ? <section className={styles.resource}>
        <div className={styles.resourceHeading}>
          <span className={styles.label}>{staff ? t("reservation.provider") : t("reservation.facility")}</span>
          {onChangeResource ? <button type="button" className={styles.change} onClick={onChangeResource} disabled={disabled} aria-label={staff ? t("reservation.summary.changeProvider") : t("reservation.summary.changeFacility")}>{t("reservation.summary.change")}</button> : null}
        </div>
        <div className={styles.resourceIdentity}>
          {photo ? <Image className={`${styles.avatar} ${staff ? styles.person : ""}`} src={photo} alt="" width={64} height={64} unoptimized /> : <span className={`${styles.avatar} ${styles.initials} ${staff ? styles.person : ""}`} aria-hidden="true">{initials(resource.name)}</span>}
          <div className={styles.resourceCopy}>
            <strong>{resource.name}</strong>
            {resource.location ? <p>{resource.location}</p> : null}
          </div>
        </div>
      </section> : null}

      {selected ? <>
        <div className={styles.service}>
          <span className={styles.label}>{t("reservation.service")}</span>
          <h3>{selected.service.name}</h3>
          <p>{t("reservation.duration", { minutes: resource?.durationMinutes ?? selected.service.durationMinutes })}</p>
        </div>
        {slot ? <div className={styles.appointment}>
          <span className={styles.label}>{t("reservation.date")}</span>
          <strong><time dateTime={slot.startAt}>{dateLabel(slot.startAt, timezone, locale)}</time></strong>
          <span><time dateTime={slot.startAt}>{timeLabel(slot.startAt, timezone, locale)}</time>–<time dateTime={slot.endAt}>{timeLabel(slot.endAt, timezone, locale)}</time></span>
        </div> : null}
        <div className={styles.total}>
          <span>{t("reservation.summary.total")}</span>
          <strong>{price ?? t("reservation.priceOnRequest")}</strong>
        </div>
      </> : <p className={styles.empty}>{t("reservation.summary.empty")}</p>}
    </div>
  </aside>;
}
